// src/integrations/providerColumns/providerColumnRegistry.js
// -----------------------------------------------------------------------------
// Thin provider column registry aggregator.
//
// Enterprise boundary:
//  • This file should stay small.
//  • Provider-specific column definitions live in provider folders:
//      src/integrations/providers/<provider>/<provider>ProviderColumns.js
//  • Adding HubSpot later should mean adding a HubSpot provider file and one
//    import here, not bloating generic table utilities.
// -----------------------------------------------------------------------------

import {
  bitlyProviderColumns,
} from '../providers/bitly/bitlyProviderColumns';
import {
  getProviderColumnSpec,
  normalizeProviderId,
} from '../../utils/providerOutputColumnUtils';

export const providerColumnRegistry = Object.freeze({
  bitly: bitlyProviderColumns,
});

export function getKnownProviderIds() {
  return Object.keys(providerColumnRegistry);
}

// Backward-compatible alias.
//
// Historically this function returned provider IDs even though the name says
// "column IDs". Keep it exported to avoid breaking existing imports, but prefer
// getKnownProviderIds() in new code.
export function getKnownProviderColumnIds() {
  return getKnownProviderIds();
}

export function getProviderColumnSet(provider) {
  const providerId = normalizeProviderId(provider);

  if (!providerId) return null;

  return providerColumnRegistry[providerId] || null;
}

export function getProviderColumnDefinitions(provider, options = {}) {
  const providerSet = getProviderColumnSet(provider);

  if (!providerSet) return [];

  if (typeof providerSet.getColumns === 'function') {
    return providerSet.getColumns(options) || [];
  }

  return Array.isArray(providerSet.columns)
    ? providerSet.columns
    : [];
}

export function getProviderColumnDefinition(column = {}, options = {}) {
  const spec = getProviderColumnSpec(column);

  if (!spec.isProviderColumn) return null;

  const definitions = getProviderColumnDefinitions(spec.provider, options);

  return definitions.find((definition) => {
    const definitionSpec = getProviderColumnSpec(definition);

    if (!definitionSpec.isProviderColumn) return false;

    if (definitionSpec.role !== spec.role) return false;
    if (definitionSpec.provider !== spec.provider) return false;

    if (spec.outputKey) {
      return definitionSpec.outputKey === spec.outputKey;
    }

    if (spec.metricKey) {
      return definitionSpec.metricKey === spec.metricKey;
    }

    return false;
  }) || null;
}

export function getProviderGroupDefinition(provider) {
  const providerSet = getProviderColumnSet(provider);

  if (!providerSet) return null;

  return {
    groupId: providerSet.groupId || providerSet.provider,
    groupLabel: providerSet.groupLabel || providerSet.provider,
    provider: providerSet.provider,
    defaultColumnId: providerSet.defaultColumnId || null,
  };
}

export function getProviderGroupDefinitions(providerIds = []) {
  const ids = Array.isArray(providerIds) && providerIds.length
    ? providerIds
    : getKnownProviderIds();

  return ids
    .map((provider) => getProviderGroupDefinition(provider))
    .filter(Boolean);
}

export function listProviderColumnDefinitions({
  providerIds = [],
  includeMetrics = false,
  includeExperimentalMetrics = false,
  sourceUfiByProvider = {},
} = {}) {
  const ids = Array.isArray(providerIds) && providerIds.length
    ? providerIds
    : getKnownProviderIds();

  return ids.flatMap((provider) => {
    const providerId = normalizeProviderId(provider);

    return getProviderColumnDefinitions(providerId, {
      includeMetrics,
      includeExperimentalMetrics,
      sourceUfi: sourceUfiByProvider?.[providerId] || null,
    });
  });
}
